import { ComputedRef, type Ref, computed } from 'vue'

import { useTr } from '@/composables/use-tr'
import { type APIGameType, CategoryShortInfo, GameCategoriesInfoType } from '@/types'

export type GenerateGameHeadParams = {
    game: Ref<APIGameType | undefined>
}

type GamePlatforms = {
    is_ios: boolean
    is_android: boolean
    is_desktop: boolean
}

function getPlatforms(platforms: GamePlatforms) {
    const result: ('ios' | 'android' | 'desktop')[] = []
    if (platforms.is_ios) {
        result.push('ios')
    }
    if (platforms.is_android) {
        result.push('android')
    }
    if (platforms.is_desktop) {
        result.push('desktop')
    }
    return result
}

export function getSubCategories(
    type: 'category' | 'tag' | 'series',
    gameCatInfo?: GameCategoriesInfoType,
): CategoryShortInfo[] {
    if (!gameCatInfo || !gameCatInfo.categoriesInfo) {
        return []
    }
    return gameCatInfo.categoriesInfo.filter((cat) => cat.categoryType === type)
}

export function useGamePlatforms(platforms: Ref<GamePlatforms | undefined>): ComputedRef<string> {
    const tr = useTr()
    return computed(() => {
        if (!platforms.value) {
            return ''
        }
        return getPlatforms(platforms.value)
            .map((platform) => {
                switch (platform) {
                    case 'desktop':
                        return tr('gameContainer.desktop')
                    case 'android':
                        return tr('gameContainer.android')
                    case 'ios':
                        return tr('gameContainer.ios')
                }
            })
            .join(', ')
    })
}

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Permissions_Policy#browser_compatibility
// this is used in both export and game frame in portal
export const iframeAllowAttribute = [
    // could be used in gameplay
    'fullscreen',
    'accelerometer',
    'camera',
    'clipboard-read',
    'clipboard-write',
    'screen-wake-lock',
    'speaker-selection',
    'web-share',
    'geolocation',
    'gyroscope',
    'microphone',
    'xr-spatial-tracking',

    // the props below are just in case
    // for now no ad/analytics/purchases are happen on game frame level

    // could be used in ad
    'autoplay',
    'encrypted-media',
    'picture-in-picture',
    // could be used in in-game purchases
    'payment',
    'publickey-credentials-get',
    'publickey-credentials-create',
    // for analytics
    'storage-access',
    'attribution-reporting',
    'browsing-topics',
].join(';')
